import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import {
  SHORTCUT_DEFINITIONS,
  isMacPlatform,
  shortcutDisplay,
  type ShortcutDefinition,
} from './shortcuts';

type ShortcutHelpDialogProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;
};

export function ShortcutHelpDialog({ open, onOpenChange }: ShortcutHelpDialogProps) {
  const { t } = useTranslation('shortcuts');
  const mac = isMacPlatform();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border border-border bg-card shadow-xl"
          data-testid="shortcut-help-dialog"
        >
          <div className="border-b border-border px-4 py-3">
            <Dialog.Title className="text-md font-semibold">
              {t('helpTitle', { defaultValue: 'Keyboard shortcuts' })}
            </Dialog.Title>
            <Dialog.Description className="text-xs text-muted-foreground">
              {t('helpDescription', {
                defaultValue: 'Press ? from anywhere to reopen this list.',
              })}
            </Dialog.Description>
          </div>
          <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto px-4 py-2 text-sm">
            {SHORTCUT_DEFINITIONS.map((def) => (
              <ShortcutRow key={def.id} def={def} mac={mac} />
            ))}
          </ul>
          <div className="flex justify-end border-t border-border px-4 py-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded border border-border px-3 py-1 text-xs hover:bg-muted"
            >
              {t('close', { defaultValue: 'Close' })}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ShortcutRow({ def, mac }: { def: ShortcutDefinition; mac: boolean }) {
  const { t } = useTranslation('shortcuts');
  const tokens = shortcutDisplay(def, mac);
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="text-foreground">
        {t(`items.${def.id}`, { defaultValue: def.labelKey })}
      </span>
      <span
        className="flex items-center gap-1 text-xs text-muted-foreground"
        aria-label={tokens.join(' + ')}
      >
        {tokens.map((token, idx) => (
          <kbd
            key={`${def.id}-${idx}`}
            className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground"
          >
            {token}
          </kbd>
        ))}
      </span>
    </li>
  );
}
